import { useState } from 'react';
import { Button, Dialog, Field, Input } from '@/components/ui';
import type { DispenseBody, DispensingRx } from '@/features/admin-v2/api';
import type { StaffDelivery } from '@/features/deliveries/types';
import styles from './dispensary.module.css';
import {
  REFUSAL_LABEL,
  isLate,
  klDayClock,
  packingSlot,
  refusedItems,
} from './delivery-format';

const noop = () => {};

type Delivery = NonNullable<DispenseBody['delivery']>;

/**
 * RL-1186: "Pack for delivery" for a requested same-day delivery. Sends the
 * dispensary's "Dispensed" step with `delivery` (address from the request,
 * slot from its deliver-by time, the courier), through the RL-1159 hook so the
 * outside-prescription confirm still applies.
 */
export function PackDeliveryDialog({
  delivery,
  rx,
  busy,
  onClose,
  onPack,
}: {
  delivery: StaffDelivery | null;
  /** The prescription in the to-dispense queue; null when it is not there. */
  rx: DispensingRx | null;
  busy: boolean;
  onClose: () => void;
  onPack: (rx: DispensingRx, delivery: Delivery) => void;
}) {
  return (
    <Dialog
      open={!!delivery}
      onClose={busy ? noop : onClose}
      dismissible={!busy}
      title="Pack for delivery"
    >
      {delivery && (
        <PackForm
          key={delivery.id}
          delivery={delivery}
          rx={rx}
          busy={busy}
          onClose={onClose}
          onPack={onPack}
        />
      )}
    </Dialog>
  );
}

function PackForm({
  delivery,
  rx,
  busy,
  onClose,
  onPack,
}: {
  delivery: StaffDelivery;
  rx: DispensingRx | null;
  busy: boolean;
  onClose: () => void;
  onPack: (rx: DispensingRx, delivery: Delivery) => void;
}) {
  const [now] = useState(() => new Date());
  const [slot, setSlot] = useState(() => packingSlot(delivery, now));
  const [courier, setCourier] = useState(delivery.courier?.name ?? '');
  const [errors, setErrors] = useState<{ slot?: string; courier?: string }>({});
  const refused = refusedItems(rx);
  const blocked = !rx || refused.length > 0;

  const submit = () => {
    if (!rx) return;
    const next = {
      slot:
        slot.trim().length < 2 || slot.trim().length > 60
          ? 'Enter the delivery slot (2 to 60 characters).'
          : undefined,
      courier:
        courier.trim().length < 2 || courier.trim().length > 60
          ? 'Enter the courier (2 to 60 characters).'
          : undefined,
    };
    setErrors(next);
    if (next.slot || next.courier) return;
    onPack(rx, {
      address: delivery.address,
      slot: slot.trim(),
      courier: courier.trim(),
    });
  };

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <dl className={styles.facts}>
        <dt>Patient</dt>
        <dd>
          {delivery.patient.fullName}
          {delivery.patient.phone && ` · ${delivery.patient.phone}`}
        </dd>
        <dt>Prescription</dt>
        <dd className="mono">{delivery.prescriptionNumber ?? '-'}</dd>
        <dt>Address</dt>
        <dd>{delivery.address}</dd>
        <dt>Deliver by</dt>
        <dd>{klDayClock(delivery.deliverBy, now)}</dd>
      </dl>
      {isLate(delivery, now) && (
        <p className={styles.warn} role="status">
          The deliver-by time has passed. Call the patient before packing.
        </p>
      )}
      {!rx && (
        <p className={styles.warn} role="status">
          This prescription is not in the to-dispense queue (already handed
          over, or not ready), so it cannot be packed here.
        </p>
      )}
      {refused.length > 0 && (
        <div className={styles.warn} role="status">
          <p>
            These medicines cannot be delivered, so this prescription cannot be
            packed. Hand it over at the counter (this cancels the request) or
            cancel the delivery with a reason.
          </p>
          <ul aria-label="Counter-only medicines" className={styles.refused}>
            {refused.map((r) => (
              <li key={r.itemId}>
                <strong>{r.medicine}</strong> — {REFUSAL_LABEL[r.reason]}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Field label="Delivery slot" error={errors.slot}>
        <Input
          value={slot}
          maxLength={60}
          onChange={(event) => setSlot(event.target.value)}
        />
      </Field>
      <Field
        label="Courier"
        hint="Who takes it, e.g. the courier company or rider."
        error={errors.courier}
      >
        <Input
          value={courier}
          maxLength={60}
          onChange={(event) => setCourier(event.target.value)}
        />
      </Field>
      <p className={styles.hint}>
        Every box scanned, labels printed and counselling ticked first: the API
        checks them. If the doctor issued an outside prescription you will be
        asked to confirm with the patient.
      </p>
      <div className={styles.formActions}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={blocked}
        >
          Pack for delivery
        </Button>
      </div>
    </form>
  );
}
